import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Camera, KeyRound, BellRing, DoorOpen, CheckCircle2, ArrowRight } from "lucide-react";

const categories = [
  {
    id: "cctv",
    icon: Camera,
    name: "CCTV Surveillance",
    headline: "High-Definition Video Surveillance",
    desc: "From single-site shops to multi-building campuses, we design IP CCTV systems with crystal-clear imaging, intelligent analytics and secure remote access.",
    features: [
      "4K / 8MP IP dome, bullet & PTZ cameras",
      "AI analytics: human/vehicle detection, face recognition, LPR",
      "Thermal cameras for perimeter & industrial monitoring",
      "NVR / server-based recording with RAID storage",
      "Mobile & web remote viewing, multi-site management",
    ],
    chips: ["4K UHD", "IP67", "WDR", "AI ANALYTICS", "POE", "ONVIF"],
    img: "https://images.unsplash.com/photo-1563920443079-783e5c786b83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "access",
    icon: KeyRound,
    name: "Access Control",
    headline: "Biometric & Card Access Systems",
    desc: "Control exactly who goes where, when. Centralised door access with audit trails, time attendance and anti-passback for offices, condos and factories.",
    features: [
      "Fingerprint, face recognition & RFID card readers",
      "Door controllers with electromagnetic locks & exit devices",
      "Time & attendance integration",
      "Lift access & floor zoning control",
      "Visitor management & intercom linkage",
    ],
    chips: ["BIOMETRIC", "RFID / MIFARE", "FACE ID", "ANTI-PASSBACK"],
    img: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "alarm",
    icon: BellRing,
    name: "Intrusion Alarm",
    headline: "Intrusion Detection & Alarm Systems",
    desc: "Layered intrusion protection with motion sensors, door contacts, glass-break detectors and sirens — with optional CMS monitoring and app alerts.",
    features: [
      "Wired & hybrid alarm panels with zone partitioning",
      "PIR motion, door/window contact & vibration sensors",
      "Panic buttons & duress signalling",
      "GSM / IP communicator for instant mobile alerts",
      "Integration with CCTV for video verification",
    ],
    chips: ["ZONE CONTROL", "GSM ALERT", "CMS READY", "24/7 ARMING"],
    img: "https://images.unsplash.com/photo-1496368077930-c1e31b4e5b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "barrier",
    icon: DoorOpen,
    name: "Gate & Turnstile",
    headline: "Automatic Barriers, Gates & Turnstiles",
    desc: "Manage vehicle and pedestrian flow with automatic barrier gates, sliding/swing auto-gates and pedestrian turnstiles tied into your access control.",
    features: [
      "Vehicle barrier gates with loop detectors & LPR options",
      "Auto-gate systems for residential & industrial entrances",
      "Tripod, flap & full-height turnstiles",
      "Long-range RFID / TAG vehicle access",
      "Fail-safe operation with battery backup",
    ],
    chips: ["LPR READY", "LONG-RANGE TAG", "FAIL-SAFE", "HEAVY DUTY"],
    img: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  },
];

export default function SecuritySystems() {
  const [active, setActive] = useState(categories[0]);

  return (
    <div data-testid="security-systems-page">
      <PageHero
        label="Security Systems"
        title="Complete Protection for People & Property"
        subtitle="CCTV surveillance, access control, intrusion alarms and gate automation — designed as one integrated security layer for your facility."
        image="https://images.unsplash.com/photo-1496368077930-c1e31b4e5b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 mb-10" data-testid="security-tabs">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c)}
                data-testid={`security-tab-${c.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border transition-colors ${
                  active.id === c.id
                    ? "bg-blue-600 text-white border-cyan-400/40"
                    : "bg-transparent text-slate-300 border-white/10 hover:border-cyan-400/40"
                }`}
              >
                <c.icon className="w-4 h-4" /> {c.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              className="glow-card overflow-hidden"
              data-testid={`security-panel-${active.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto">
                  <img src={active.img} alt={active.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F172A]/40" />
                </div>
                <div className="p-8 lg:p-12">
                  <span className="tech-label">{active.name}</span>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white mt-2">
                    {active.headline}
                  </h2>
                  <p className="mt-4 text-slate-300 leading-relaxed">{active.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {active.features.map((f) => (
                      <li key={f} className="flex gap-3 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {active.chips.map((c) => <span key={c} className="spec-chip">{c}</span>)}
                  </div>
                  <Link
                    to="/quote"
                    data-testid={`security-quote-cta-${active.id}`}
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors border border-cyan-400/40"
                  >
                    Request {active.name} Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
